import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import { Icon, Spacing } from '@reservamos/elements';
import { useTranslation } from 'react-i18next';
import checkboxRenderer from './CheckboxRenderer';
import InsuranceModal from './InsuranceModal';
import { formatCurrency } from '../utils/currency';
import 'styles/components/CardCheckbox';
import useCurrencyConfig from '../hooks/whitelabel/useCurrencyConfig';
import useInsuranceConfig from '../hooks/useInsuranceConfig';
import useWhitelabelEnvs from '../hooks/whitelabel/useWhitelabelEnvs';

/**
 * Passenger insurance checkbox component
 * @param {Object} props - Component props
 * @param {string} props.passenger - Passenger identifier
 * @param {number} props.insuranceOutgoingUnitAmount - Cost of outgoing insurance
 * @param {number} props.insuranceIncomingUnitAmount - Cost of incoming insurance
 * @param {boolean} props.disabled - Whether the checkbox is disabled
 * @returns JSX Element
 */
const PassengerInsuranceCheckbox = ({
  passenger,
  insuranceOutgoingUnitAmount,
  insuranceIncomingUnitAmount,
  disabled,
}) => {
  const { t } = useTranslation('purchase');
  const { currency, locale } = useCurrencyConfig();
  const { brand } = useWhitelabelEnvs();
  // Brand-aware insurance icon (falls back to the ETN default for unmapped brands). Gontijo shows
  // Aurora's SVG (INS-1113); previously this component hardcoded the ETN icon for every brand.
  const { icon } = useInsuranceConfig();

  return (
    <div className="card-checkbox">
      <Icon type={icon} hasMargin />
      <div className="card-checkbox-wrapper">
        <Spacing size="S" vertical>
          <InsuranceModal />
          {insuranceOutgoingUnitAmount > 0 && (
            <Field
              id={`${passenger}.wantsOutgoingInsurance`}
              name={`${passenger}.wantsOutgoingInsurance`}
              type="checkbox"
              label={t('label.add_departing_trip_insurance', {
                cost: formatCurrency(insuranceOutgoingUnitAmount, 2, currency, locale),
                context: brand,
              })}
              component={checkboxRenderer}
              disabled={disabled}
            />
          )}
          {insuranceIncomingUnitAmount > 0 && (
            <Field
              id={`${passenger}.wantsIncomingInsurance`}
              name={`${passenger}.wantsIncomingInsurance`}
              type="checkbox"
              label={t('label.add_return_trip_insurance', {
                cost: formatCurrency(insuranceIncomingUnitAmount, 2, currency, locale),
                context: brand,
              })}
              component={checkboxRenderer}
              disabled={disabled}
            />
          )}
        </Spacing>
      </div>
    </div>
  );
};

PassengerInsuranceCheckbox.propTypes = {
  passenger: PropTypes.string.isRequired,
  insuranceOutgoingUnitAmount: PropTypes.number.isRequired,
  insuranceIncomingUnitAmount: PropTypes.number,
  disabled: PropTypes.bool,
};

export default PassengerInsuranceCheckbox;
